/**
 * Persónuverndarstefna (Act 90/2018 / GDPR) — the policy text itself. Text lives in i18n
 * (settings.privacy.*). No router hooks: it is shown both inside the app (/privacy) and before
 * sign-in (sign-in screen, invite link, onboarding consent step).
 */
import { ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card } from '../../components/ui';
import { useT, type MessageKey } from '../../i18n';

interface Block {
  heading: MessageKey;
  paragraphs?: MessageKey[];
  bullets?: MessageKey[];
  after?: MessageKey[];
}

const BLOCKS: Block[] = [
  { heading: 'settings.privacy.controller_h', paragraphs: ['settings.privacy.controller'] },
  {
    heading: 'settings.privacy.stored_h',
    bullets: [
      'settings.privacy.stored_1',
      'settings.privacy.stored_2',
      'settings.privacy.stored_3',
      'settings.privacy.stored_4',
      'settings.privacy.stored_5',
      'settings.privacy.stored_6',
    ],
    after: ['settings.privacy.password'],
  },
  { heading: 'settings.privacy.purpose_h', paragraphs: ['settings.privacy.purpose', 'settings.privacy.basis'] },
  {
    heading: 'settings.privacy.who_h',
    bullets: ['settings.privacy.who_1', 'settings.privacy.who_2', 'settings.privacy.who_3', 'settings.privacy.who_4', 'settings.privacy.who_5'],
  },
  { heading: 'settings.privacy.processors_h', paragraphs: ['settings.privacy.processors'] },
  { heading: 'settings.privacy.retention_h', paragraphs: ['settings.privacy.retention'] },
  { heading: 'settings.privacy.device_h', paragraphs: ['settings.privacy.device'] },
  { heading: 'settings.privacy.calendar_h', paragraphs: ['settings.privacy.calendar'] },
  { heading: 'settings.privacy.cookies_h', paragraphs: ['settings.privacy.cookies'] },
  { heading: 'settings.privacy.rights_h', paragraphs: ['settings.privacy.rights'] },
];

function P({ children }: { children: ReactNode }) {
  return <p className="leading-relaxed text-[var(--ink-2)]">{children}</p>;
}

export function PrivacyPolicy({ flat }: { flat?: boolean }) {
  const t = useT();
  return (
    <article className="mx-auto flex max-w-2xl flex-col gap-6">
      <header className="flex items-start gap-4">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--accent-soft)] text-[var(--accent)]">
          <ShieldCheck className="size-6" aria-hidden />
        </div>
        <div className="flex flex-col gap-2">
          <P>{t('settings.privacy.intro')}</P>
          <p className="text-[15px] text-[var(--muted)]">{t('settings.privacy.updated')}</p>
        </div>
      </header>
      {BLOCKS.map((b) => {
        const body = (
          <>
            <h2 className="text-xl font-bold">{t(b.heading)}</h2>
            {b.paragraphs?.map((k) => <P key={k}>{t(k)}</P>)}
            {b.bullets ? (
              <ul className="flex list-disc flex-col gap-1.5 pl-6 text-[var(--ink-2)] marker:text-[var(--accent)]">
                {b.bullets.map((k) => (
                  <li key={k} className="leading-relaxed">
                    {t(k)}
                  </li>
                ))}
              </ul>
            ) : null}
            {b.after?.map((k) => <P key={k}>{t(k)}</P>)}
          </>
        );
        // Inside a sheet the cards would nest card-in-card; plain sections read better there.
        return flat ? (
          <section key={b.heading} className="flex flex-col gap-3">
            {body}
          </section>
        ) : (
          <Card as="section" key={b.heading} className="flex flex-col gap-3 p-5 md:p-6">
            {body}
          </Card>
        );
      })}
    </article>
  );
}
